import { useMutation, useQuery } from '@apollo/client/react';
import { every, map, some } from 'lodash';
import { useContext } from 'react';

import CurrentUserContext from '../CurrentUser';

import PermissionContext, { type AccessAction, type AccessEntity } from '.';

import { MyAccessDocument, GetMyGrantRequestsDocument, CreateGrantRequestDocument } from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

interface PermissionsContextProviderProps {
  children: React.ReactNode;
}

const PermissionsContextProvider = ({ children }: PermissionsContextProviderProps) => {
  const { currentUser } = useContext(CurrentUserContext);

  const {
    data: accessData,
    refetch: refetchPermissions,
    loading: loadingPermissions,
  } = useQuery(MyAccessDocument, {
    skip: !currentUser || !currentUser.basicOnboardingCompleted,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: requestsData, loading: loadingRequests } = useQuery(GetMyGrantRequestsDocument, {
    skip: !currentUser,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { status: 'pending' },
  });

  const [createRequest] = useMutation(CreateGrantRequestDocument, {
    refetchQueries: ['getMyGrantRequests'],
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'A request for access has been sent to the administration. You will be notified via an email regarding its status.',
      });
    },
    onError: () => {
      displayNotification({
        type: 'error',
        text: 'An access request could not be generated at the moment. Please try again later.',
      });
    },
  });

  const grants = accessData?.myAccess;

  const findGrant = (action: AccessAction, entity: AccessEntity) =>
    grants?.find((g) => g.action === action && g.entity === entity);

  /**
   * Precise check mirroring the backend checker: a project-scoped grant only
   * satisfies checks made with a matching projectId; company-wide grants
   * satisfy everything. Owners hold the full registry (the backend already
   * expands it in myAccess).
   */
  const can = (action: AccessAction, entity: AccessEntity, opts?: { projectId?: string }): boolean => {
    if (!currentUser) return false;
    if (currentUser.isOwner) return true;

    const grant = findGrant(action, entity);
    if (!grant) return false;
    if (grant.projectIds == null) return true;
    if (!opts?.projectId) return false;

    return grant.projectIds.includes(String(opts.projectId));
  };

  /** Held anywhere (company-wide or any project) — for page/nav visibility. */
  const canAnywhere = (
    action: AccessAction,
    entity: AccessEntity | AccessEntity[],
    condition: 'or' | 'and' | undefined = 'or',
  ): boolean => {
    if (!currentUser) return false;
    if (currentUser.isOwner) return true;

    if (!Array.isArray(entity)) return Boolean(findGrant(action, entity));

    const lodashOp = condition === 'or' ? some : every;

    return lodashOp(map(entity, (ent) => Boolean(findGrant(action, ent))));
  };

  const requestPermission = (action: AccessAction, entity: AccessEntity, projectId?: string) => {
    createRequest({
      variables: {
        action: String(action),
        entity: String(entity),
        ...(projectId && { projectId }),
      },
    });
  };

  const checkRequestExists = (action: AccessAction, entity: AccessEntity): boolean => {
    return Boolean(requestsData?.getMyGrantRequests?.find((r) => r.action === action && r.entity === entity));
  };

  const context = {
    grants,
    loadingPermissions,
    loadingRequests,
    refetchPermissions,
    can,
    canAnywhere,
    canPerformAction: canAnywhere,
    requestPermission,
    checkRequestExists,
  };

  return <PermissionContext.Provider value={context}>{children}</PermissionContext.Provider>;
};

export default PermissionsContextProvider;
